import { Component } from '@theme/component';

/**
 * @typedef {Object} ManifestoItemsRefs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<ManifestoItemsRefs>} */
export class ManifestoItemsComponent extends Component {
  requiredRefs = ['container'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #stabilizeRafId = null;
  /** @type {number | null} */
  #lastStabilizedHeight = null;
  #isMeasuring = false;

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#updateActiveTab();
    this.#stabilizeLayoutHeight();
    this.#setupEventListeners();
    this.setAttribute('data-initialized', '');

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#isMeasuring) return;
      this.#scheduleStabilizeLayoutHeight();
    });

    for (const panel of this.refs.panels ?? []) {
      this.#resizeObserver.observe(panel);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#stabilizeRafId !== null) {
      cancelAnimationFrame(this.#stabilizeRafId);
      this.#stabilizeRafId = null;
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#abort?.abort();
    this.#abort = null;
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (event) => this.#handleKeydown(event), opts);

    for (const [index, tab] of tabs.entries()) {
      tab.addEventListener('click', (event) => this.#handleTabClick(event, index), opts);
      tab.addEventListener('focus', (event) => this.#handleTabFocus(event, index), opts);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const index = tabs.indexOf(target);
    const navMap = {
      ArrowUp: -1,
      ArrowDown: 1,
      Home: -index,
      End: tabs.length - 1 - index,
    };

    const offset = navMap[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      const nextIndex = (index + offset + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
      this.#activate(nextIndex);
    }
  }

  /**
   * @param {MouseEvent} event
   * @param {number} index
   */
  #handleTabClick(event, index) {
    event.preventDefault();
    this.#activate(index);
  }

  /**
   * @param {FocusEvent} event
   * @param {number} index
   */
  #handleTabFocus(event, index) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches(':focus-visible')) {
      this.#activate(index);
    }
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;

    this.#active = index;
    this.#updateActiveTab();
    this.#scheduleStabilizeLayoutHeight();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [index, tab] of tabs?.entries() ?? []) {
      const isActive = index === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [index, panel] of panels?.entries() ?? []) {
      const isActive = index === this.#active;
      panel.toggleAttribute('hidden', !isActive);
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }

  #scheduleStabilizeLayoutHeight() {
    if (this.#stabilizeRafId !== null) return;

    this.#stabilizeRafId = requestAnimationFrame(() => {
      this.#stabilizeRafId = null;
      this.#stabilizeLayoutHeight();
    });
  }

  /** Lock layout height to the tallest tab state so sibling blocks in the column stay stable. */
  #stabilizeLayoutHeight() {
    const { container, panels } = this.refs;
    if (!container || !panels?.length) return;

    const maxHeight = this.#measureMaxLayoutHeight();
    if (maxHeight === this.#lastStabilizedHeight) return;

    this.#lastStabilizedHeight = maxHeight;
    const minHeight = `${maxHeight}px`;
    if (container.style.minHeight !== minHeight) {
      container.style.minHeight = minHeight;
    }
  }

  #measureMaxLayoutHeight() {
    const { container, panels } = this.refs;
    if (!container || !panels?.length) return 0;

    this.#isMeasuring = true;
    this.#resizeObserver?.disconnect();

    const activeIndex = this.#active;
    let maxHeight = 0;

    for (let i = 0; i < panels.length; i++) {
      for (const [index, panel] of panels.entries()) {
        const isActive = index === i;
        panel.toggleAttribute('hidden', !isActive);
        panel.toggleAttribute('inert', !isActive);
      }
      maxHeight = Math.max(maxHeight, container.offsetHeight);
    }

    this.#active = activeIndex;
    this.#updateActiveTab();

    for (const panel of panels) {
      this.#resizeObserver?.observe(panel);
    }

    this.#isMeasuring = false;

    return maxHeight;
  }
}

customElements.define('manifesto-items-component', ManifestoItemsComponent);
